import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchVoiceRegions, setVoiceRegion, type VoiceRegionRow } from '../api/catalog';
import { ca } from '../i18n/ca';

type Choice = '' | 'ISLAND' | 'CONTINENTAL';

/** What the select shows for a row: the admin's value, or '' when the survey decides. */
const choiceOf = (row: VoiceRegionRow | null): Choice =>
  row?.override === 'ISLAND' || row?.override === 'CONTINENTAL' ? row.override : '';

/** A world with no survey and no override: continental by default, as the platform answers it. */
const defaultRow = (world: string): VoiceRegionRow => ({
  world,
  kind: 'continental',
  source: 'default',
  why: 'no survey',
  override: null,
});

/** One row's answer in words: «illa · segons la inspecció — la massa de terra més gran no toca la vora del mapa». */
function Answer({ row }: { row: VoiceRegionRow }) {
  const v = ca.voiceRegion;
  return (
    <>
      <strong>{v.kinds[row.kind] ?? row.kind}</strong>
      {' · '}
      <span className={row.source === 'override' ? 'chip' : 'muted'}>
        {v.sources[row.source] ?? row.source}
      </span>
      <span className="muted small"> — {v.why(row.why)}</span>
    </>
  );
}

/** The admin's select: island, continent, or the survey's own answer. */
function RegionSelect({
  row,
  busy,
  label,
  onPick,
}: {
  row: VoiceRegionRow;
  busy: boolean;
  label: string;
  onPick: (choice: Choice) => void;
}) {
  const v = ca.voiceRegion;
  return (
    <select
      className="control"
      aria-label={label}
      value={choiceOf(row)}
      disabled={busy}
      onChange={(e) => onPick(e.target.value as Choice)}
    >
      <option value="">{v.fromSurvey}</option>
      <option value="ISLAND">{v.setIsland}</option>
      <option value="CONTINENTAL">{v.setContinental}</option>
    </select>
  );
}

/**
 * The terrain page's «Accent de les veus» (release D, step D2.3): island or
 * continent, where the answer came from and why, and for an admin the select
 * that fixes it or gives it back to the survey. Read from the same table the
 * catalogue shows; a reader the platform refuses sees nothing.
 */
export function VoiceRegionField({ world, canEdit }: { world: string; canEdit: boolean }) {
  const v = ca.voiceRegion;
  const [row, setRow] = useState<VoiceRegionRow | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRow(undefined);
    fetchVoiceRegions()
      .then((rows) => {
        if (!live) return;
        const mine = rows.find((r) => r.world.toLowerCase() === world.toLowerCase());
        setRow(mine ?? defaultRow(world));
      })
      .catch(() => live && setRow(null));
    return () => {
      live = false;
    };
  }, [world]);

  if (!row) return null;

  const pick = (choice: Choice) => {
    setBusy(true);
    setError(null);
    setVoiceRegion(world, choice === '' ? null : choice)
      .then((saved) => setRow(saved))
      .catch(() => setError(v.saveError))
      .finally(() => setBusy(false));
  };

  return (
    <section className="card" aria-label={v.title}>
      <h2 className="section">{v.title}</h2>
      <p>
        <Answer row={row} />
      </p>
      {canEdit && (
        <p className="row">
          <RegionSelect row={row} busy={busy} label={v.label} onPick={pick} />
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="muted small">{v.intro}</p>
    </section>
  );
}

/**
 * Every surveyed terrain's accent in one table (release D, step D2.3): the
 * list the owner checks against the maps he knows, opened on demand because
 * it reads every survey. An admin fixes a row in place.
 */
export function VoiceRegionTable({ canEdit }: { canEdit: boolean }) {
  const v = ca.voiceRegion;
  const [rows, setRows] = useState<VoiceRegionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchVoiceRegions()
      .then(setRows)
      .catch(() => setError(v.loadError));
  }, [v.loadError]);

  const pick = (world: string, choice: Choice) => {
    setBusy(world);
    setError(null);
    setSaved(null);
    setVoiceRegion(world, choice === '' ? null : choice)
      .then((row) => {
        setRows((old) => (old ?? []).map((r) => (r.world === world ? row : r)));
        setSaved(v.saved(row.world, v.kinds[row.kind] ?? row.kind));
      })
      .catch(() => setError(v.saveError))
      .finally(() => setBusy(null));
  };

  return (
    <details
      className="card voice-regions"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && rows === null) load();
      }}
    >
      <summary>
        <strong>{v.tableTitle}</strong>
        {rows && <span className="chip">{rows.length}</span>}
      </summary>
      <p className="muted small">{v.tableIntro}</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p className="notice ok small" role="status">
          {saved}
        </p>
      )}
      {rows && rows.length === 0 && <p className="muted">{v.empty}</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{v.colWorld}</th>
                <th>{v.colKind}</th>
                <th>{v.colSource}</th>
                <th>{v.colWhy}</th>
                {canEdit && <th>{v.colSet}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.world}>
                  <td>
                    <Link to={`/catalog/terrain/${encodeURIComponent(r.world)}`}>{r.world}</Link>
                  </td>
                  <td>
                    <strong>{v.kinds[r.kind] ?? r.kind}</strong>
                  </td>
                  <td>
                    <span className={r.source === 'override' ? 'chip' : 'muted small'}>
                      {v.sources[r.source] ?? r.source}
                    </span>
                  </td>
                  <td className="muted small">{v.why(r.why)}</td>
                  {canEdit && (
                    <td>
                      <RegionSelect
                        row={r}
                        busy={busy === r.world}
                        label={`${v.label}: ${r.world}`}
                        onPick={(choice) => pick(r.world, choice)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
